import * as Api from '@ViewModels';
import { ActionItemViewModel } from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import { format } from 'date-fns';
import * as React from 'react';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { getDisplayName } from '../../../../models/NameUtils';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { Button } from '../../../components-v2/Button';
import {
	Dialog,
	DialogClose,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogPopup,
	DialogTitle,
} from '../../../components-v2/Dialog';
import {
	Drawer,
	DrawerClose,
	DrawerContent,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
} from '../../../components-v2/Drawer';
import { Label } from '../../../components-v2/Label';
import { THEME } from '../../../components-v2/Theme/theme';
import { LoadingSpinner } from '../../LoadingSpinner';
import { brandPrimary, header, urgentDate } from '../../../styles/colors';
import { styleSheet as flyoutStyleSheet } from '../../dataBoards/styles';
import { Separator } from '../../Separator';
import { ActionItemCheckbox } from '../ActionItemCheckbox';
import { ActionItemFlyoutEditor } from '../ActionItemEditor/ActionItemFlyoutEditor';
import { AssignDueDateButton } from '../AssignDueDateButton';
import { AssigneeButton } from '../AssigneeButton';

interface IProps {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	actionItem: Api.IActionItem;
	startInEdit?: boolean;
	onChanged?: () => void;
}

/**
 * Side flyout for an action item — the single, consistent action-item surface. In view mode it
 * shows the item (complete/due/assignee inline edits + the contacts it's about); "Edit" swaps to
 * the native {@link ActionItemFlyoutEditor} (built from the shared action-item editor machinery).
 * View and edit share one `Drawer` instance and one header, so toggling between them doesn't
 * close/reopen the panel or swap the chrome. Changes persist through the action-item VM and call
 * {@link IProps.onChanged} to refetch.
 */
export const ActionItemDetailsFlyout: React.FC<IProps> = ({
	isOpen,
	setIsOpen,
	actionItem,
	startInEdit,
	onChanged,
}) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { postNotification } = useLocalNotificationService<Api.IActionItem>();
	const checkboxId = React.useId();
	const [pendingDelete, setPendingDelete] = React.useState<(() => void) | null>(null);

	const title = actionItem.preview || actionItem.plainTextContent || 'Action item';
	const creatorName = actionItem.creator ? getDisplayName(actionItem.creator) : null;
	const dueDate = actionItem.dueDate ? new Date(actionItem.dueDate) : undefined;
	const aboutContacts = (actionItem.referencedEntities?.contacts ?? []).map(ref => ref.entity).filter(Boolean);

	const [isEditing, setIsEditing] = React.useState(false);
	const [editorVm, setEditorVm] = React.useState<ActionItemViewModel | null>(null);

	const exitToView = React.useCallback(() => {
		setIsEditing(false);
		setEditorVm(null);
	}, []);

	React.useEffect(() => {
		if (!isOpen) {
			setIsEditing(false);
			setEditorVm(null);
		} else if (startInEdit) {
			setIsEditing(true);
		}
	}, [isOpen, startInEdit]);

	React.useEffect(() => {
		if (!isEditing || editorVm) {
			return;
		}
		let cancelled = false;
		const vm = new ActionItemViewModel(userSession, { id: actionItem.id });
		const done = () => {
			if (!cancelled) {
				setEditorVm(vm);
			}
		};
		vm.load().then(done, done);
		return () => {
			cancelled = true;
		};
	}, [isEditing, editorVm, actionItem.id, userSession]);

	// Inline edits post EDIT so the subscribed list refreshes, and surface failures rather than swallow them.
	const onEdited = (saved: Api.IActionItem) => {
		postNotification({ info: saved, topic: Topics.EDIT_ACTION_ITEM });
		onChanged?.();
	};
	const onEditError = (error: Api.IOperationResultNoValue) => errorMessages.pushApiError(error);
	const onToggleComplete = (complete: boolean) => {
		const vm = new ActionItemViewModel(userSession, actionItem);
		Promise.resolve(vm.toggleComplete(complete)).then(onEdited, onEditError);
	};
	const persist = (apply: (model: Api.IActionItem) => void) => {
		const vm = new ActionItemViewModel(userSession, actionItem);
		const model = vm.toActionItem();
		apply(model);
		Promise.resolve(vm.save(model)).then(onEdited, onEditError);
	};
	const onDueDateSelected = (date: Date) => persist(model => (model.dueDate = date.toISOString()));
	const onAssigneeSelected = (user: Api.IUser) => persist(model => (model.assignee = user));

	// Posts DELETE so subscribed lists drop it, then closes; surfaces errors and stays open on failure.
	const onDelete = () => {
		const vm = new ActionItemViewModel(userSession, actionItem);
		const promise = vm.delete();
		if (!promise) {
			return;
		}
		promise.then(
			() => {
				postNotification({ info: vm.toActionItem(), topic: Topics.DELETE_ACTION_ITEM });
				onChanged?.();
				setIsOpen(false);
			},
			(error: Api.IOperationResultNoValue) => {
				errorMessages.pushApiError(error);
			}
		);
	};

	return (
		<Drawer
			open={isOpen}
			onOpenChange={open => {
				if (!open && isEditing) {
					exitToView();
					return;
				}
				setIsOpen(open);
			}}
			swipeDirection='right'
		>
			<DrawerContent>
				<DrawerHeader>
					<DrawerTitle>Action Item</DrawerTitle>
				</DrawerHeader>
				{isEditing ? (
					editorVm ? (
						<ActionItemFlyoutEditor
							actionItem={editorVm}
							autoFocus
							onCancel={exitToView}
							onSaved={() => {
								onChanged?.();
								exitToView();
							}}
							onDeleted={() => {
								onChanged?.();
								setIsOpen(false);
							}}
							onDeleteRequested={performDelete => setPendingDelete(() => performDelete)}
						/>
					) : (
						<div className={css(styleSheet.loading)}>
							<LoadingSpinner type='large' />
						</div>
					)
				) : (
					<>
						<div className={css(flyoutStyleSheet.flyoutBody)}>
							<div className={css(styleSheet.container)}>
								<section className={css(styleSheet.headerSection)}>
									<h4 className={css(flyoutStyleSheet.subTitle, actionItem.isCompleted && styleSheet.titleCompleted)}>
										{title}
									</h4>
									<span className={css(styleSheet.headerMeta)}>
										{actionItem.isCompleted ? 'Completed' : 'In progress'}
									</span>
								</section>

								<Separator />

								<section className={css(styleSheet.detailsSection)}>
									<Label htmlFor={checkboxId} styles={[styleSheet.statusRow]}>
										<ActionItemCheckbox
											id={checkboxId}
											checked={!!actionItem.isCompleted}
											onChange={e => onToggleComplete(e.currentTarget.checked)}
										/>
										<span className={css(styleSheet.statusLabel)}>
											{actionItem.isCompleted ? 'Completed' : 'Mark complete'}
										</span>
									</Label>

									<div className={css(styleSheet.detailRow)}>
										<span className={css(styleSheet.label)}>Due date</span>
										<div className={css(styleSheet.control)}>
											<AssignDueDateButton dueDate={dueDate} onDaySelected={onDueDateSelected}>
												<span className={css(styleSheet.controlLabel)}>
													{dueDate ? format(dueDate, 'MMM d, yyyy') : 'Set due date'}
												</span>
											</AssignDueDateButton>
										</div>
									</div>

									<div className={css(styleSheet.detailRow)}>
										<span className={css(styleSheet.label)}>Assigned to</span>
										<div className={css(styleSheet.control)}>
											<AssigneeButton
												assignee={actionItem.assignee}
												userSession={userSession}
												onAssigneeSelected={onAssigneeSelected}
											>
												<span className={css(styleSheet.controlLabel)}>
													{actionItem.assignee ? getDisplayName(actionItem.assignee) : 'Assign to…'}
												</span>
											</AssigneeButton>
										</div>
									</div>

									{creatorName ? (
										<div className={css(styleSheet.detailRow)}>
											<span className={css(styleSheet.label)}>Created by</span>
											<span className={css(styleSheet.value)}>{creatorName}</span>
										</div>
									) : null}
								</section>

								{aboutContacts.length > 0 ? (
									<>
										<Separator />
										<section className={css(styleSheet.aboutSection)}>
											<h4 className={css(styleSheet.aboutTitle)}>About</h4>
											{aboutContacts.map(contact => (
												<div className={css(styleSheet.contactRow)} key={contact.id}>
													<span className={css(styleSheet.contactName)}>{getDisplayName(contact)}</span>
													{contact.primaryEmail?.value ? (
														<span className={css(styleSheet.contactEmail)}>{contact.primaryEmail.value}</span>
													) : null}
												</div>
											))}
										</section>
									</>
								) : null}

								<Separator />

								<div className={css(styleSheet.actionRow)}>
									<Button variant='outline' onClick={() => setIsEditing(true)}>
										Edit
									</Button>
									<Button
										variant='ghost'
										styles={[styleSheet.deleteButton]}
										onClick={() => setPendingDelete(() => onDelete)}
									>
										Delete
									</Button>
								</div>
							</div>
						</div>
						<DrawerFooter>
							<DrawerClose
								render={props => (
									<Button variant='secondary' {...props}>
										Close
									</Button>
								)}
							/>
						</DrawerFooter>
					</>
				)}
			</DrawerContent>
			<Dialog
				open={!!pendingDelete}
				onOpenChange={open => {
					if (!open) {
						setPendingDelete(null);
					}
				}}
			>
				<DialogPopup>
					<DialogHeader>
						<DialogTitle>Delete action item?</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete this action item? Once deleted it will be gone forever.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<DialogClose
							render={props => (
								<Button variant='secondary' {...props}>
									Cancel
								</Button>
							)}
						/>
						<Button
							variant='destructive'
							onClick={() => {
								pendingDelete?.();
								setPendingDelete(null);
							}}
						>
							Delete
						</Button>
					</DialogFooter>
				</DialogPopup>
			</Dialog>
		</Drawer>
	);
};

const styleSheet = StyleSheet.create({
	container: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.md,
	},
	headerSection: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		textAlign: 'center',
		padding: THEME.spacing.md,
		gap: THEME.spacing.xs,
	},
	titleCompleted: {
		textDecoration: 'line-through',
		opacity: 0.7,
	},
	headerMeta: {
		fontSize: 13,
		color: header,
	},
	detailsSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: 14,
		padding: THEME.spacing.md,
	},
	statusRow: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
		cursor: 'pointer',
	},
	statusLabel: {
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
		color: THEME.colors.text.main,
		whiteSpace: 'nowrap',
	},
	detailRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		gap: THEME.spacing.md,
		minHeight: 28,
	},
	label: {
		color: header,
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.bold,
		flexShrink: 0,
	},
	control: {
		display: 'flex',
		justifyContent: 'flex-end',
		minWidth: 0,
	},
	controlLabel: {
		fontSize: 13,
		whiteSpace: 'nowrap',
	},
	value: {
		fontSize: 13,
		textAlign: 'right',
	},
	aboutSection: {
		display: 'flex',
		flexDirection: 'column',
		gap: THEME.spacing.sm,
		padding: THEME.spacing.md,
	},
	aboutTitle: {
		color: brandPrimary,
		fontWeight: THEME.typography.fontWeights.bold,
		fontSize: THEME.typography.fontSizes.sm,
		margin: 0,
		marginBottom: THEME.spacing.xs,
	},
	contactRow: {
		display: 'flex',
		flexDirection: 'column',
		gap: 2,
	},
	contactName: {
		fontSize: 13,
		fontWeight: THEME.typography.fontWeights.semibold,
		color: THEME.colors.text.main,
	},
	contactEmail: {
		fontSize: THEME.typography.fontSizes.xxs,
		color: header,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	actionRow: {
		display: 'flex',
		justifyContent: 'center',
		gap: THEME.spacing.sm,
		paddingLeft: THEME.spacing.md,
		paddingRight: THEME.spacing.md,
	},
	deleteButton: {
		color: urgentDate,
	},
	loading: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		padding: THEME.spacing.xxxl,
	},
});
